import { useEffect, useRef, useState, useCallback, ChangeEvent } from "react";
import { Params, useParams, useNavigate } from "react-router-dom";
import { Alert, Button, CircularProgress, IconButton, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as LoaderPlaceholder } from "../../../assets/placeholder--loader.icon.svg";
import { ReactComponent as SmilePlaceholder } from "../../../assets/placeholder--sad-smile.icon.svg";
import { ReactComponent as CloseIcon } from "../../../assets/cross.icon.svg";
import { ReactComponent as SearchIcon } from "../../../assets/search.icon.svg";
import { ReactComponent as UploadIcon } from "../../../assets/cloud--upload.icon.svg";
import { ReactComponent as TrashIcon } from "../../../assets/trash.icon.svg";
import { ReactComponent as RefreshIcon } from "../../../assets/refresh.icon.svg";
import { FirebaseAuthContainer } from "../../../common/containers/auth/firebase-auth-container.util";
import { ProvidersContainer } from "../../../common/containers/providers/providers.container";
import CrawlBanner, {
    CrawlSyncState
} from "../../../components/admin/crawl-banner/crawl-banner.comp";
import { ThemeContainer } from "../../../common/containers/theme/theme.container";
import { ToastContainer } from "../../../components/toast/toast.comp";
import {
    fetchContentFolders,
    fetchCrawlRuns,
    fetchLearningObject,
    fetchLearningObjects,
    deleteLearningObjects,
    setLearningObjectsVisibility,
    getLearningObjectDownloadUrl,
    checkNeedsAccessToken,
    refetchPage,
    excludePage
} from "../../../api/admin/content-providers/endpoints-referrers";
import {
    subscribeForceUpdate,
    forceFileProcessing,
    reindexLearningObject
} from "../../../api/admin/knowledge-base/endpoints-referrers";
import { publishRecognitionResult } from "../../../api/admin/recognition-result/endpoints-referrers";
import { Logo } from "../../../common/enums/logo.enum";
import { ProviderType } from "../../../common/enums/provider-type.enum";
import { CitationDisplay } from "../../../common/enums/citation-display.enum";
import { FileType } from "../../../common/enums/file-type.enum";
import { SortType } from "../../../common/enums/sort-type.enum";
import { LearningObjectStatus } from "../../../common/enums/learning-object-status.enum";
import { Provider } from "../../../common/interfaces/admin/provider.interface";
import { Folder } from "../../../common/interfaces/admin/folder.interface";
import { MenuItem } from "../../../common/interfaces/comps/menu-item.interface";
import { RecognitionResult } from "../../../common/interfaces/admin/recognition-result.interface";
import {
    TableLearningObject,
    LearningObject
} from "../../../common/interfaces/admin/learning-object.interface";
import EmptyLeftMenuLayout from "../../../components/layout/empty-left-menu-layout.comp";
import HeaderLayout from "../../../components/layout/header-layout.comp";
import { LoaderView } from "../../../components/loader/loader.comp";
import {
    ActionMenu,
    CheckboxMenuFilterT,
    SelectionFilter
} from "../../../components/menu-filter/menu-filter.comp";
import CoursesTree from "../../../components/admin/courses-tree/courses-tree.comp";
import LearningObjects from "../../../components/admin/learning-objects/learning-objects.comp";
import VisibilityDialog, {
    VisibilitySaveValues
} from "../../../components/admin/visibility-dialog/visibility-dialog.comp";
import FileUploadView from "../file-upload/file-upload";
import { ModalTemplate } from "../../../components/modal-template/modal-template.comp";
import { RecognitionChunksDialog } from "../../../components/admin/recognition-chunks-dialog/recognition-chunks-dialog.comp";
import {
    ReprocessConfirmationModal,
    ReprocessOptions
} from "../../../components/modals/reprocess-confirmation/reprocess-confirmation-modal.comp";
import ConfirmationPopup, {
    ConfirmationPopupType
} from "../../../components/confirmation-popup/confirmation-popup.comp";
import { loStatusName } from "../../../common/utilities/learning-object/learning-object.util";
import { useOneDriveAuthPopup } from "../../../common/utilities/onedrive-auth/useOneDriveAuthPopup";

/** Host of a web site provider's first seed URL, for the crawl banner. */
const providerHost = (url?: string): string => {
    if (!url) return "";
    try {
        return new URL(url).host;
    } catch {
        return "";
    }
};

const KnowledgeBaseElements = () => {
    const DEFAULT_PAGE_INDEX = 1;
    const DEFAULT_PAGE_SIZE = 50;

    const navigate = useNavigate();
    const { providerId, courseId } = useParams<Params<string>>();
    const { logo } = ThemeContainer.useContainer();
    const { user, isSuperAdmin } = FirebaseAuthContainer.useContainer();
    const { providers, loadProviders } = ProvidersContainer.useContainer();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [currentCourse, setCurrentCourse] = useState<Folder>();
    const [folderTree, setFolderTree] = useState<Folder[]>([]);
    const [learningObjects, setLearningObjects] = useState<TableLearningObject[]>([]);
    const [checkedLOs, setCheckedLOs] = useState<TableLearningObject[]>([]);
    const [selectionMode, setSelectionMode] = useState<boolean>(false);
    const [isFileUploaderProvider, setIsFileUploaderProvider] = useState<boolean>(false);
    const [deletableUids, setDeletableUids] = useState<string[]>([]);
    const [loTotalCount, setLoTotalCount] = useState<number>(0);
    const [pageIndex, setPageIndex] = useState<number>(DEFAULT_PAGE_INDEX);
    const [searchString, setSearchString] = useState<string>("");
    const [searchParam, setSearchParam] = useState<string>("");
    const [sortList] = useState<string[]>(Object.values(SortType));
    const [types] = useState<MenuItem<string>[]>(
        Object.values(FileType).map(type => ({ label: type.firstLetterToUppercase(), value: type }))
    );
    const [statuses] = useState<MenuItem<string>[]>(
        Object.values(LearningObjectStatus).map(status => ({
            label: loStatusName(status),
            value: status
        }))
    );
    const [selectedSort, setSelectedSort] = useState<string>(SortType.az);
    const [checkedTypes, setCheckedTypes] = useState<MenuItem<string>[]>([]);
    const [checkedStatuses, setCheckedStatuses] = useState<MenuItem<string>[]>([]);
    const [editableRR, setEditableRR] = useState<RecognitionResult>();
    const [scrollUpCounter, setScrollUpCounter] = useState<number>(0);
    const [filesUploadOpen, setFilesUploadOpen] = useState<boolean>(false);
    const [deleteLOsConfirmationOpen, setDeleteLOsConfirmationOpen] = useState<boolean>(false);
    const [publishConfirmationOpen, setPublishConfirmationOpen] = useState<boolean>(false);
    const [unpublishConfirmationOpen, setUnpublishConfirmationOpen] = useState<boolean>(false);
    const [publishSubtitle, setPublishSubtitle] = useState<string>();
    const [chunksDialogOpen, setChunksDialogOpen] = useState<boolean>(false);
    const [chunksDialogLO, setChunksDialogLO] = useState<TableLearningObject | null>(null);
    const [msAccessToken, setMSAccessToken] = useState<string>("");
    const [reprocessTarget, setReprocessTarget] = useState<
        | { kind: "provider" }
        | { kind: "lo"; item: TableLearningObject }
        | { kind: "bulk"; items: TableLearningObject[] }
        | null
    >(null);
    const [filtersLoadedForCourse, setFiltersLoadedForCourse] = useState<string | null>(null);
    const [visibilityDialog, setVisibilityDialog] = useState<{
        loItemIds: string[];
        single: boolean;
        citationDisplay?: CitationDisplay | null;
    } | null>(null);
    const currentCourseRef = useRef<Folder>();
    const checkedTypesRef = useRef<MenuItem<string>[]>([]);
    const checkedStatusesRef = useRef<MenuItem<string>[]>([]);
    const selectedSortRef = useRef<string>(SortType.az);
    const searchParamRef = useRef<string>("");
    const providerIdRef = useRef<number | null>(null);

    const currentProvider = providers.find(p => p.id === Number(providerId));
    const isOneDriveProvider = currentProvider?.name === ProviderType.oneDrive;

    // Bulk-import progress: seeded from the provider row, kept live by
    // provider_sync SSE events. Drives the import banner during the
    // otherwise-blind scan/create window (before LOs exist to show status).
    const [providerSync, setProviderSync] = useState<CrawlSyncState>({});
    // Web scraper review gate (D16): the run awaiting review, resolved from
    // the runs list whenever the provider is in awaiting_review, so the
    // banner's Review button can link straight to it.
    const [reviewRun, setReviewRun] = useState<{
        id: string;
        expiresAt?: string | null;
        trigger?: string | null;
    } | null>(null);
    const isWebScraperProvider = currentProvider?.name === ProviderType.webScraper;
    // Web site per-page actions (BR-36): the page awaiting the exclude confirmation.
    const [excludeTarget, setExcludeTarget] = useState<TableLearningObject | null>(null);

    const { showToast } = ToastContainer.useContainer();
    // A refused per-page action (409 while a crawl runs or awaits review) is
    // reported as a toast; replacing the banner state with "error" hid the
    // awaiting-review banner and its Review button until a reload.
    const providerActionError = (error: any, fallback: string) =>
        showToast(error?.response?.data?.message ?? fallback, 6000);

    const handleRefetchPage = (item: TableLearningObject) => {
        if (!user || !providerId) return;
        refetchPage(user.companyId, Number(providerId), item.loItemId)
            .then(() => setProviderSync(prev => ({ ...prev, status: "scanning", error: null })))
            .catch(error => providerActionError(error, "The page could not be re-fetched"));
    };

    const handleExcludePageConfirm = () => {
        if (!user || !providerId || !excludeTarget) return;
        const target = excludeTarget;
        setExcludeTarget(null);
        excludePage(user.companyId, Number(providerId), target.loItemId)
            .then(() => {
                setLearningObjects(prev => prev.filter(lo => lo.loItemId !== target.loItemId));
                setLoTotalCount(prev => Math.max(0, (prev ?? 1) - 1));
                loadProviders();
            })
            .catch(error => providerActionError(error, "The page could not be excluded"));
    };

    // Auth popup for OneDrive reprocessing (only enabled when viewing a OneDrive provider)
    const { openAuth: openMSAuth } = useOneDriveAuthPopup({
        enabled: isOneDriveProvider,
        onSuccess: accessToken => {
            setMSAccessToken(accessToken);
        },
        onError: error => {
            console.error("Microsoft auth error during reprocess:", error);
        }
    });

    const publishDescription = `When you publish one version of the document,
    all other versions will be unpublished. ${logo === Logo.mba ? "MBAssist" : "Knowledge Base"} will use published
    version for answering questions regarding this document.`;
    const unpublishDescription = `When you unpublish all documents, ${logo === Logo.mba ? "MBAssist" : "Knowledge Base"} will no longer be
    able to answer questions regarding this document.`;

    useEffect(() => {
        if (!providers.length || providerId) return;

        const provider = providers.find(provider => provider.name === ProviderType.fileUploader);

        if (provider) {
            navigate(`/knowledge-base/${provider.id}`);
        }
    }, [navigate, providers, providerId]);

    useEffect(() => {
        const currentProviderId = Number(providerId);
        if (!user || !providers.length || isNaN(currentProviderId)) return;

        if (!providers.find(provider => provider.id === currentProviderId)) {
            navigate("/knowledge-base");
            return;
        }

        const isFileUploader =
            providers.find(
                provider =>
                    provider.id === currentProviderId && provider.name === ProviderType.fileUploader
            ) !== undefined;
        setIsFileUploaderProvider(isFileUploader);
        // Selection (checkboxes + Action menu) is available on every provider so
        // visibility can be bulk-edited. Delete/Upload stay fileUploader-only.
        setSelectionMode(true);

        fetchContentFolders(user.companyId, currentProviderId).then(response => {
            // TODO response type will be changed
            setFolderTree(response.data.length ? response.data[0].courses : []);

            const provider: Provider = response.data.length ? response.data[0] : undefined;
            const course =
                !courseId && provider?.courses?.length === 1 ? provider.courses[0] : undefined;
            if (course) {
                navigate(`/knowledge-base/${providerId}/${course.courseId}`);
            }
        });
    }, [navigate, user, providerId, providers, courseId]);

    useEffect(() => {
        if (user && currentCourse) {
            // Wait until per-course filters have been loaded from localStorage
            // for the current course; otherwise we'd fire a default-filter fetch
            // that races the post-load fetch and can overwrite the filtered
            // list when its response arrives last.
            if (filtersLoadedForCourse !== currentCourse.courseId) return;
            loadLearningObjects(
                user.companyId,
                currentCourse.courseId,
                createQuery(
                    DEFAULT_PAGE_INDEX,
                    DEFAULT_PAGE_SIZE,
                    searchParam,
                    checkedTypes,
                    checkedStatuses,
                    selectedSort
                )
            );
        } else {
            setLearningObjects([]);
        }
    }, [
        user,
        currentCourse,
        searchParam,
        checkedTypes,
        checkedStatuses,
        selectedSort,
        filtersLoadedForCourse
    ]);

    // Filters are persisted to localStorage per-course so they survive a
    // navigation round-trip (e.g. clicking "AI Result" → returning here via
    // browser back). On course change, filters are loaded from localStorage
    // for the new course; on filter change, they're written back.
    const filtersKey = (courseId: string) => `kb-elements-filters:${courseId}`;
    const lastLoadedCourseRef = useRef<string | undefined>();

    useEffect(() => {
        if (!currentCourse) return;

        let stored: { types?: string[]; statuses?: string[]; sort?: string } = {};
        try {
            const raw = localStorage.getItem(filtersKey(currentCourse.courseId));
            if (raw) stored = JSON.parse(raw);
        } catch (e) {
            console.warn("kb-elements: could not parse stored filters", e);
        }

        setSearchParam("");
        setSearchString("");
        setCheckedTypes(
            (stored.types || [])
                .map(v => types.find(t => t.value === v))
                .filter((t): t is MenuItem<string> => !!t)
        );
        setCheckedStatuses(
            (stored.statuses || [])
                .map(v => statuses.find(s => s.value === v))
                .filter((s): s is MenuItem<string> => !!s)
        );
        setSelectedSort(
            stored.sort ||
                (currentCourse?.slugName === "text-uploader" ? SortType.newestFirst : SortType.az)
        );
        setScrollUpCounter(0);
        lastLoadedCourseRef.current = currentCourse.courseId;
        setFiltersLoadedForCourse(currentCourse.courseId);
    }, [currentCourse, types, statuses]);

    // Persist filter state to localStorage. Skip the very first run after a
    // course switch — that one's writing the values we just loaded back, which
    // is a no-op but adds noise.
    useEffect(() => {
        if (!currentCourse) return;
        if (lastLoadedCourseRef.current !== currentCourse.courseId) return;
        try {
            localStorage.setItem(
                filtersKey(currentCourse.courseId),
                JSON.stringify({
                    types: checkedTypes.map(t => t.value),
                    statuses: checkedStatuses.map(s => s.value),
                    sort: selectedSort
                })
            );
        } catch (e) {
            console.warn("kb-elements: could not persist filters", e);
        }
    }, [currentCourse, checkedTypes, checkedStatuses, selectedSort]);

    useEffect(() => {
        setCurrentCourse(
            courseId && !!folderTree.length
                ? folderTree.find(item => item.courseId === courseId)
                : undefined
        );
    }, [courseId, folderTree]);

    useEffect(() => {
        const callSearchService = () => {
            setSearchParam(searchString);
        };

        const debouncer = setTimeout(() => {
            callSearchService();
        }, 1000);

        return () => {
            clearTimeout(debouncer);
        };
    }, [searchString]);

    useEffect(() => {
        currentCourseRef.current = currentCourse;
    }, [currentCourse]);

    useEffect(() => {
        checkedTypesRef.current = checkedTypes;
    }, [checkedTypes]);

    useEffect(() => {
        checkedStatusesRef.current = checkedStatuses;
    }, [checkedStatuses]);

    useEffect(() => {
        selectedSortRef.current = selectedSort;
    }, [selectedSort]);

    useEffect(() => {
        searchParamRef.current = searchParam;
    }, [searchParam]);

    // The provider rows feed the banner seed below and the left menu; they are
    // only refetched on actions, so an import that finished while this view
    // was not mounted would otherwise re-seed a stale "scanning" banner.
    useEffect(() => {
        if (providerId) loadProviders();
    }, [providerId, loadProviders]);

    useEffect(() => {
        providerIdRef.current = providerId ? Number(providerId) : null;
        setProviderSync(prev => {
            const seed = {
                status: currentProvider?.syncStatus,
                total: currentProvider?.syncTotal,
                error: currentProvider?.syncError
            };
            // The provider rows are refetched after a terminal sync event, and
            // the row carries no crawl counters. When the status is unchanged
            // keep what the event delivered (review counts, discovered,
            // fetched); the banner otherwise read "nothing found" until a
            // reload re-resolved the pending run.
            return prev.status === seed.status ? { ...prev, ...seed } : seed;
        });
    }, [
        providerId,
        currentProvider?.syncStatus,
        currentProvider?.syncTotal,
        currentProvider?.syncError
    ]);

    // Resolve the run awaiting review (and its counts) for the banner.
    useEffect(() => {
        if (!user || !isWebScraperProvider || providerSync.status !== "awaiting_review") {
            setReviewRun(null);
            return;
        }
        const currentProviderId = Number(providerId);
        if (Number.isNaN(currentProviderId)) return;
        fetchCrawlRuns(user.companyId, currentProviderId)
            .then(response => {
                const pending = response.data.find(run => run.status === "awaiting_review");
                if (!pending) {
                    setReviewRun(null);
                    return;
                }
                setReviewRun({
                    id: pending.id,
                    expiresAt: pending.expiresAt,
                    trigger: pending.trigger
                });
                const c = pending.counts || {};
                setProviderSync(prev => ({
                    ...prev,
                    reviewCounts: prev.reviewCounts ?? {
                        new: c.new ?? 0,
                        modified: c.modified ?? 0,
                        renamed: c.renamed ?? 0,
                        removed: c.removed ?? 0,
                        unchanged: c.unchanged ?? 0,
                        skipped: c.skipped ?? 0,
                        failed: c.failed ?? 0
                    }
                }));
            })
            .catch(() => setReviewRun(null));
    }, [user, providerId, isWebScraperProvider, providerSync.status]);

    useEffect(() => {
        if (!user) return;

        const eventSource = subscribeForceUpdate(user.companyId);

        eventSource.onmessage = ({ data }: { data: string }) => {
            try {
                const message = JSON.parse(data);

                if (
                    message.type === "lo_status_change" &&
                    message.courseId === currentCourseRef.current?.courseId
                ) {
                    setLearningObjects(prevLOs =>
                        prevLOs.map(lo =>
                            lo.loItemId === message.loItemId
                                ? {
                                      ...lo,
                                      status: message.loStatus,
                                      name: message.loName ?? lo.name,
                                      updatedAt: message.loUpdatedAt ?? lo.updatedAt
                                  }
                                : lo
                        )
                    );
                } else if (
                    message.type === "lo_insert" &&
                    message.courseId === currentCourseRef.current?.courseId
                ) {
                    const types = checkedTypesRef.current;
                    const statuses = checkedStatusesRef.current;
                    const search = searchParamRef.current;
                    const sort = selectedSortRef.current;

                    const matchesType =
                        types.length === 0 || types.some(t => t.value === message.loSourceType);
                    const matchesStatus =
                        statuses.length === 0 || statuses.some(s => s.value === message.loStatus);
                    const matchesSearch =
                        !search ||
                        (message.loName ?? "").toLowerCase().includes(search.toLowerCase());

                    if (!matchesType || !matchesStatus || !matchesSearch) return;

                    const newItem = {
                        companyId: user.companyId,
                        courseId: message.courseId,
                        loItemId: message.loItemId,
                        name: message.loName,
                        sourceType: message.loSourceType,
                        status: message.loStatus,
                        createdAt: message.loCreatedAt,
                        updatedAt: message.loCreatedAt,
                        recognitionResults: [],
                        showRR: false
                    } as TableLearningObject;

                    setLearningObjects(prevLOs => {
                        if (prevLOs.some(lo => lo.loItemId === message.loItemId)) {
                            return prevLOs;
                        }
                        const newList = [...prevLOs, newItem];
                        setLoTotalCount(prev => prev + 1);
                        switch (sort) {
                            case SortType.newestFirst:
                                return newList.sort(
                                    (a, b) =>
                                        new Date(b.createdAt).getTime() -
                                        new Date(a.createdAt).getTime()
                                );
                            case SortType.oldestFirst:
                                return newList.sort(
                                    (a, b) =>
                                        new Date(a.createdAt).getTime() -
                                        new Date(b.createdAt).getTime()
                                );
                            case SortType.za:
                                return newList.sort((a, b) => b.name.localeCompare(a.name));
                            case SortType.az:
                            default:
                                return newList.sort((a, b) => a.name.localeCompare(b.name));
                        }
                    });
                } else if (
                    message.type === "rr_status_change" &&
                    message.courseId === currentCourseRef.current?.courseId
                ) {
                    setLearningObjects(prevLOs =>
                        prevLOs.map(lo => {
                            if (lo.loItemId !== message.loItemId) return lo;

                            const existingRR = lo.recognitionResults.find(
                                rr => rr.uid === message.rrUid
                            );

                            if (existingRR) {
                                return {
                                    ...lo,
                                    recognitionResults: lo.recognitionResults.map(rr =>
                                        rr.uid === message.rrUid
                                            ? {
                                                  ...rr,
                                                  status: message.rrStatus,
                                                  name: message.rrName ?? rr.name,
                                                  updatedAt: message.rrUpdatedAt ?? rr.updatedAt
                                              }
                                            : rr
                                    )
                                };
                            }

                            // RR not yet in frontend state (LO arrived via lo_insert SSE
                            // with empty recognitionResults) — append it
                            const newRR = {
                                uid: message.rrUid,
                                companyId: user?.companyId ?? "",
                                courseId: message.courseId,
                                loItemId: message.loItemId,
                                name: message.rrName ?? "Recognition Result",
                                version: message.rrVersion ?? 1,
                                status: message.rrStatus,
                                isPublish: message.rrIsPublish ?? false,
                                hasUpdates: message.rrHasUpdates ?? true,
                                createdAt: message.rrCreatedAt ?? new Date().toISOString(),
                                updatedAt: message.rrUpdatedAt ?? new Date().toISOString(),
                                knowladgeBaseId: "",
                                recognitionResultData: {} as any
                            } as RecognitionResult;

                            return {
                                ...lo,
                                recognitionResults: [...lo.recognitionResults, newRR]
                            };
                        })
                    );
                } else if (
                    message.type === "rr_delete" &&
                    message.courseId === currentCourseRef.current?.courseId
                ) {
                    setLearningObjects(prevLOs =>
                        prevLOs.map(lo => {
                            if (lo.loItemId !== message.loItemId) return lo;
                            return {
                                ...lo,
                                recognitionResults: lo.recognitionResults.filter(
                                    rr => rr.uid !== message.rrUid
                                )
                            };
                        })
                    );
                } else if (
                    message.type === "lo_delete" &&
                    message.courseId === currentCourseRef.current?.courseId
                ) {
                    setLearningObjects(prevLOs => {
                        const filtered = prevLOs.filter(lo => lo.loItemId !== message.loItemId);
                        if (filtered.length < prevLOs.length) {
                            setLoTotalCount(prev => prev - 1);
                        }
                        return filtered;
                    });
                } else if (message.type === "provider_sync") {
                    if (message.contentItemProviderId === providerIdRef.current) {
                        setProviderSync(prev => ({
                            status: message.syncStatus,
                            total: message.syncTotal,
                            error: message.syncError,
                            // Crawl counters ride progress-only events; keep the
                            // last known values when an event omits them.
                            discovered: message.discovered ?? prev.discovered,
                            fetched: message.fetched ?? prev.fetched,
                            reviewCounts: message.reviewCounts ?? prev.reviewCounts
                        }));
                    }
                    // Terminal phases: refetch the provider rows so the left
                    // menu and later banner seeds carry the live status.
                    if (
                        message.syncStatus === "processing" ||
                        message.syncStatus === "error" ||
                        message.syncStatus === "awaiting_review" ||
                        message.syncStatus === null
                    ) {
                        loadProviders();
                    }
                }
            } catch {
                // ignore non-JSON messages (e.g. heartbeats)
            }
        };

        return () => {
            eventSource.close();
        };
    }, [user, loadProviders]);

    const createQuery = (
        page: number,
        pageSize: number,
        search: string,
        checkedTypes: MenuItem<string>[],
        checkedStatuses: MenuItem<string>[],
        selectedSort: string
    ) => {
        const queryParts: string[] = [];

        queryParts.push(`page=${page}`);
        queryParts.push(`pageSize=${pageSize}`);
        queryParts.push(`search=${search}`);

        if (checkedTypes.length > 0) {
            queryParts.push(`sourceType=${checkedTypes.map(type => type.value).join(",")}`);
        }

        if (checkedStatuses.length > 0) {
            queryParts.push(`status=${checkedStatuses.map(type => type.value).join(",")}`);
        }

        const sortMap = {
            [SortType.newestFirst]: "sortBy=createdAt&sortOrder=desc",
            [SortType.oldestFirst]: "sortBy=createdAt&sortOrder=asc",
            [SortType.az]: "sortBy=name&sortOrder=asc",
            [SortType.za]: "sortBy=name&sortOrder=desc"
        };
        if (selectedSort && sortMap[selectedSort as SortType]) {
            queryParts.push(sortMap[selectedSort as SortType]);
        }

        return `?${queryParts.join("&")}`;
    };

    const loadLearningObjects = (companyId: number, courseId: string, query?: string) => {
        setIsLoading(true);
        fetchLearningObjects(companyId, courseId, query).then(response => {
            const {
                data: { count, items }
            } = response;
            setScrollUpCounter(prevValue => prevValue + 1);
            setLoTotalCount(count);
            setLearningObjects(items.map((item: LearningObject) => ({ ...item, showRR: false })));
            setPageIndex(DEFAULT_PAGE_INDEX);
            setIsLoading(false);
        });
    };

    const appendLearningObjects = (companyId: number, courseId: string, query?: string) => {
        fetchLearningObjects(companyId, courseId, query).then(response => {
            const {
                data: { count, items }
            } = response;
            setLoTotalCount(count);
            setLearningObjects(prevItems => [
                ...prevItems,
                ...items.map((item: LearningObject) => ({ ...item, showRR: false }))
            ]);
            setPageIndex(prevIndex => prevIndex + 1);
        });
    };

    const handleAppendLearningObjects = useCallback(() => {
        if (!user || !currentCourse) return;

        appendLearningObjects(
            user.companyId,
            currentCourse.courseId,
            createQuery(
                pageIndex + 1,
                DEFAULT_PAGE_SIZE,
                searchParam,
                checkedTypes,
                checkedStatuses,
                selectedSort
            )
        );
    }, [user, currentCourse, pageIndex, searchParam, checkedTypes, checkedStatuses, selectedSort]);

    const handleSearchChange = (evt: ChangeEvent<HTMLInputElement>) => {
        const val = evt.target?.value;
        setSearchString(val ?? "");
    };

    const clearSearchString = () => {
        setSearchString("");
    };

    const handleChangePublishStatus = (rr: RecognitionResult, loName: string) => {
        setEditableRR(rr);
        setPublishSubtitle(`${loName} : ${rr.name} v.${rr.version}`);
        rr.isPublish ? setUnpublishConfirmationOpen(true) : setPublishConfirmationOpen(true);
    };

    const handleDeleteCheckedLearningObjects = () => {
        const uids = checkedLOs.map((item: LearningObject) => item.loItemId);
        if (uids.length > 0) {
            handleDeleteLearningObjects(uids);
        }
    };

    const handleDeleteLearningObjects = (uids: string[]) => {
        setDeletableUids(uids);
        setDeleteLOsConfirmationOpen(true);
    };

    const handleDeleteLOsConfirmationAction = () => {
        if (!user || !courseId || !deletableUids.length) return;

        deleteLearningObjects(user.companyId, courseId, deletableUids).then(() => {
            setLearningObjects(prevLOs => {
                const filtered = prevLOs.filter(
                    lo => !deletableUids.find(uid => uid === lo.loItemId)
                );
                const removedCount = prevLOs.length - filtered.length;
                if (removedCount > 0) {
                    setLoTotalCount(prevValue => prevValue - removedCount);
                }
                return filtered;
            });
            setCheckedLOs([]);
        });

        setDeleteLOsConfirmationOpen(false);
    };

    const openSingleVisibility = useCallback((item: TableLearningObject) => {
        setVisibilityDialog({
            loItemIds: [item.loItemId],
            single: true,
            citationDisplay: item.citationDisplay ?? null
        });
    }, []);

    const openBulkVisibility = useCallback(() => {
        if (!checkedLOs.length) return;
        setVisibilityDialog({
            loItemIds: checkedLOs.map(lo => lo.loItemId),
            single: false
        });
    }, [checkedLOs]);

    const handleSaveVisibility = useCallback(
        (values: VisibilitySaveValues) => {
            if (!user || !courseId || !visibilityDialog) return;
            const ids = visibilityDialog.loItemIds;
            setVisibilityDialog(null);
            setLearningObjectsVisibility(user.companyId, courseId, {
                loItemIds: ids,
                ...values
            }).then(response => {
                const byId = new Map<string, LearningObject>(
                    response.data.map(lo => [lo.loItemId, lo] as [string, LearningObject])
                );
                setLearningObjects(prev =>
                    prev.map(lo => {
                        const updated = byId.get(lo.loItemId);
                        return updated
                            ? {
                                  ...lo,
                                  citationDisplay: updated.citationDisplay,
                                  managerDownload: updated.managerDownload,
                                  effectiveCitationDisplay: updated.effectiveCitationDisplay,
                                  effectiveManagerDownload: updated.effectiveManagerDownload
                              }
                            : lo;
                    })
                );
            });
        },
        [user, courseId, visibilityDialog]
    );

    const handleDownloadLearningObject = useCallback(
        (item: TableLearningObject) => {
            if (!user || !courseId) return;

            // Authoritative, managerDownload-enforced endpoint. The server
            // resolves the file and returns a presigned URL (or 403 if the
            // manager may not download); the client never handles bucket/path.
            getLearningObjectDownloadUrl(user.companyId, courseId, item.loItemId)
                .then(response => {
                    const url = response.data?.url;
                    if (!url) return;
                    // Fetch the file as a blob to force download
                    fetch(url)
                        .then(res => res.blob())
                        .then(blob => {
                            const blobUrl = window.URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = blobUrl;
                            link.setAttribute("download", item.name);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(blobUrl);
                        });
                })
                .catch(() => {
                    // Forbidden (blocked manager) or no downloadable file.
                });
        },
        [user, courseId]
    );

    const getReprocessTokens = () => {
        if (isOneDriveProvider && msAccessToken) {
            return { oneAccessToken: msAccessToken };
        }
        return undefined;
    };

    const handleForceReprocessProvider = async (options: ReprocessOptions) => {
        if (!user || !providerId || !user.currentKnowledgeBaseId) return;
        try {
            // Check if any files need re-download from provider
            if (isOneDriveProvider && !msAccessToken) {
                const response = await checkNeedsAccessToken(user.companyId, Number(providerId));
                if (response.data.needsAccessToken) {
                    openMSAuth();
                    return;
                }
            }
            await forceFileProcessing(
                user.companyId,
                user.currentKnowledgeBaseId,
                { contentItemProviderId: Number(providerId) },
                getReprocessTokens(),
                options
            );
        } catch (error) {
            console.error("Force reprocess error:", error);
        }
    };

    const handleForceReprocessLearningObject = async (
        item: TableLearningObject,
        options: ReprocessOptions
    ) => {
        if (!user || !user.currentKnowledgeBaseId || !courseId) return;
        try {
            // Check if the file needs re-download from provider
            if (isOneDriveProvider && !msAccessToken) {
                const response = await fetchLearningObject(user.companyId, courseId, item.loItemId);
                const lo = response.data;
                const rr =
                    lo.recognitionResults?.find((r: any) => r.isPublish) ||
                    lo.recognitionResults?.[0];
                const file = rr?.recognitionResultData?.file as any;
                if (file && !file.bucketPath) {
                    openMSAuth();
                    return;
                }
            }
            await forceFileProcessing(
                user.companyId,
                user.currentKnowledgeBaseId,
                { courseId: item.courseId, learningObjectId: item.loItemId },
                getReprocessTokens(),
                options
            );
        } catch (error) {
            console.error("Force reprocess error:", error);
        }
    };

    const handleReprocessConfirm = (options: ReprocessOptions) => {
        const target = reprocessTarget;
        setReprocessTarget(null);
        if (!target) return;
        if (target.kind === "provider") {
            handleForceReprocessProvider(options);
        } else if (target.kind === "lo") {
            handleForceReprocessLearningObject(target.item, options);
        } else if (target.kind === "bulk") {
            target.items.forEach(lo => handleForceReprocessLearningObject(lo, options));
        }
    };

    const handleReindexLearningObject = async (item: TableLearningObject) => {
        if (!user || !user.currentKnowledgeBaseId) return;
        try {
            await reindexLearningObject(user.companyId, user.currentKnowledgeBaseId, item.loItemId);
        } catch (error) {
            console.error("Reindex error:", error);
        }
    };

    const handleViewChunks = useCallback((item: TableLearningObject) => {
        setChunksDialogLO(item);
        setChunksDialogOpen(true);
    }, []);

    const handleChunksDialogClose = useCallback(() => {
        setChunksDialogOpen(false);
        setChunksDialogLO(null);
    }, []);

    const handlePublishConfirmationAction = () => {
        if (!user || !courseId || !editableRR) return;

        publishRecognitionResult(user.companyId, editableRR.uid, true).then(() => {
            fetchLearningObject(user.companyId, courseId, editableRR.loItemId).then(response => {
                const currentLO = response.data;
                setLearningObjects(loList =>
                    loList.map(lo =>
                        lo.loItemId === editableRR.loItemId
                            ? {
                                  ...lo,
                                  status: currentLO.status,
                                  recognitionResults: currentLO.recognitionResults
                              }
                            : lo
                    )
                );
            });
        });
        setPublishConfirmationOpen(false);
    };

    const handleUnpublishConfirmationAction = () => {
        if (!user || !courseId || !editableRR) return;

        publishRecognitionResult(user.companyId, editableRR.uid, false).then(() => {
            fetchLearningObject(user.companyId, courseId, editableRR.loItemId).then(response => {
                const currentLO = response.data;
                setLearningObjects(loList =>
                    loList.map(lo =>
                        lo.loItemId === editableRR.loItemId
                            ? {
                                  ...lo,
                                  status: currentLO.status,
                                  recognitionResults: currentLO.recognitionResults
                              }
                            : lo
                    )
                );
            });
        });
        setUnpublishConfirmationOpen(false);
    };

    const selectCourse = (courseId: string) => {
        navigate(`/knowledge-base/${providerId}/${courseId}`);
    };

    const viewRecognitionResult = (uid: string) => {
        navigate(`/recognition-result/${providerId}/${uid}`);
    };

    const handleFilesUpload = () => {
        setFilesUploadOpen(true);
    };

    const handleFilesUploadClose = (needUpdate: boolean) => {
        setFilesUploadOpen(false);

        if (needUpdate && user && currentCourse) {
            loadLearningObjects(
                user.companyId,
                currentCourse.courseId,
                createQuery(
                    DEFAULT_PAGE_INDEX,
                    DEFAULT_PAGE_SIZE,
                    searchParam,
                    checkedTypes,
                    checkedStatuses,
                    selectedSort
                )
            );
        }
    };

    return (
        <EmptyLeftMenuLayout
            leftMenu={
                folderTree.length > 1 ? (
                    <CoursesTree
                        list={folderTree}
                        selectedCourse={courseId}
                        selectCourse={selectCourse}
                    />
                ) : undefined
            }
        >
            <HeaderLayout blurHeader={false}>
                <div className="knowledge-base">
                    {isWebScraperProvider && currentProvider && (
                        <CrawlBanner
                            providerName={currentProvider.displayName}
                            host={providerHost(currentProvider.providerUrl)}
                            sync={providerSync}
                            expiresAt={reviewRun?.expiresAt}
                            trigger={reviewRun?.trigger}
                            reviewAvailable={!!reviewRun}
                            onReview={() => {
                                if (reviewRun)
                                    navigate(
                                        `/knowledge-base/${currentProvider.id}/review/${reviewRun.id}`
                                    );
                            }}
                        />
                    )}
                    {!isWebScraperProvider &&
                        (providerSync.status === "scanning" ||
                            providerSync.status === "creating") && (
                            <Alert
                                className="knowledge-base__import-banner"
                                severity="info"
                                icon={<CircularProgress size={18} />}
                            >
                                {providerSync.status === "scanning"
                                    ? `Importing from ${currentProvider?.displayName || "source"}: scanning for files…`
                                    : `Importing from ${currentProvider?.displayName || "source"}: adding ${
                                          providerSync.total
                                              ? `~${providerSync.total} files`
                                              : "files"
                                      }… They will appear below as they are created.`}
                            </Alert>
                        )}
                    {providerSync.status === "error" && (
                        <Alert className="knowledge-base__import-banner" severity="error">
                            {`Import from ${currentProvider?.displayName || "source"} failed${
                                providerSync.error ? `: ${providerSync.error}` : ""
                            }. Use Refresh on the source to retry.`}
                        </Alert>
                    )}
                    {currentCourse && (
                        <div className="knowledge-base-title">
                            <span
                                className="knowledge-base-title__text"
                                role="heading"
                                aria-level={1}
                            >
                                {currentCourse.slugName === "text-uploader"
                                    ? "Uploaded files"
                                    : currentCourse.name}
                            </span>
                            <div className="knowledge-base-title-search">
                                <SvgIcon
                                    className="knowledge-base-title-search__icon"
                                    component={SearchIcon}
                                    inheritViewBox
                                />
                                <input
                                    type="text"
                                    aria-label="Search"
                                    placeholder="Search"
                                    onChange={handleSearchChange}
                                    value={searchString}
                                />
                                {!!searchString && (
                                    <IconButton
                                        className="knowledge-base-title-search__button--clear"
                                        onClick={clearSearchString}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                )}
                            </div>
                        </div>
                    )}
                    {currentCourse && (
                        <>
                            <div className="knowledge-base-filters">
                                <div className="knowledge-base-filters__left">
                                    {selectionMode && (
                                        <div className="knowledge-base-filter knowledge-base-filter--actions">
                                            <ActionMenu
                                                label="Action"
                                                disabled={checkedLOs.length === 0}
                                                actions={[
                                                    ...(isFileUploaderProvider
                                                        ? [
                                                              {
                                                                  name: "Delete",
                                                                  action: handleDeleteCheckedLearningObjects
                                                              }
                                                          ]
                                                        : []),
                                                    {
                                                        name: "Set visibility…",
                                                        action: openBulkVisibility
                                                    },
                                                    ...(isSuperAdmin
                                                        ? [
                                                              {
                                                                  name: "Reprocess",
                                                                  action: () => {
                                                                      setReprocessTarget({
                                                                          kind: "bulk",
                                                                          items: checkedLOs
                                                                      });
                                                                  }
                                                              },
                                                              {
                                                                  name: "Reindex",
                                                                  action: () => {
                                                                      checkedLOs.forEach(lo =>
                                                                          handleReindexLearningObject(
                                                                              lo
                                                                          )
                                                                      );
                                                                  }
                                                              }
                                                          ]
                                                        : [])
                                                ]}
                                                minWidth={160}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="knowledge-base-filter-group">
                                    <div className="knowledge-base-filter knowledge-base-filter--types">
                                        <CheckboxMenuFilterT
                                            label="Type"
                                            list={types}
                                            checkedList={checkedTypes}
                                            setCheckedList={setCheckedTypes}
                                            showCounter={true}
                                            rightPosition={true}
                                            minWidth={180}
                                        />
                                    </div>
                                    <div className="knowledge-base-filter knowledge-base-filter--status">
                                        <CheckboxMenuFilterT
                                            label="Status"
                                            list={statuses}
                                            checkedList={checkedStatuses}
                                            setCheckedList={setCheckedStatuses}
                                            showCounter={true}
                                            rightPosition={true}
                                            minWidth={180}
                                        />
                                    </div>
                                    <div className="knowledge-basee-filter knowledge-base-filter--sorting">
                                        <SelectionFilter
                                            list={sortList}
                                            selectedItem={selectedSort}
                                            setSelectedItem={setSelectedSort}
                                            rightPosition={true}
                                            minWidth={180}
                                        />
                                    </div>
                                </div>
                                <div className="knowledge-base-filters__right">
                                    {isFileUploaderProvider && (
                                        <Button
                                            className="knowledge-base-upload-button"
                                            variant="text"
                                            startIcon={
                                                <SvgIcon
                                                    className="knowledge-base-upload-button__icon"
                                                    component={UploadIcon}
                                                    inheritViewBox
                                                />
                                            }
                                            onClick={handleFilesUpload}
                                        >
                                            Upload File(s)
                                        </Button>
                                    )}
                                    {isSuperAdmin && (
                                        <Button
                                            className="knowledge-base-upload-button"
                                            variant="text"
                                            startIcon={
                                                <SvgIcon
                                                    className="knowledge-base-upload-button__icon"
                                                    component={RefreshIcon}
                                                    inheritViewBox
                                                />
                                            }
                                            onClick={() => setReprocessTarget({ kind: "provider" })}
                                        >
                                            Reprocess All
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="knowledge-base-table">
                                <LearningObjects
                                    totalCount={loTotalCount}
                                    items={learningObjects}
                                    setItems={setLearningObjects}
                                    checkedItems={checkedLOs}
                                    setCheckedItems={setCheckedLOs}
                                    selectionMode={selectionMode}
                                    placeholderIcon={
                                        currentCourse.slugName === "text-uploader"
                                            ? SmilePlaceholder
                                            : LoaderPlaceholder
                                    }
                                    placeholderDescription={
                                        currentCourse.slugName === "text-uploader"
                                            ? "To use this wise AI assistant, give it something to think about. Upload your documents, and it will analyze them to answer all your questions. Enjoy!"
                                            : "There are no files to show"
                                    }
                                    placeholderButtonIcon={UploadIcon}
                                    placeholderButtonTitle="Upload File(s)"
                                    placeholderAction={
                                        currentCourse.slugName === "text-uploader"
                                            ? handleFilesUpload
                                            : undefined
                                    }
                                    scrollUpCounter={scrollUpCounter}
                                    webSite={isWebScraperProvider}
                                    handleAppendFiles={handleAppendLearningObjects}
                                    handleChangePublishStatus={handleChangePublishStatus}
                                    handleAIResult={viewRecognitionResult}
                                    handleDelete={
                                        isFileUploaderProvider
                                            ? handleDeleteLearningObjects
                                            : undefined
                                    }
                                    handleDownload={handleDownloadLearningObject}
                                    handleForceReprocess={
                                        isSuperAdmin
                                            ? (item: TableLearningObject) =>
                                                  setReprocessTarget({ kind: "lo", item })
                                            : undefined
                                    }
                                    handleReindex={
                                        isSuperAdmin ? handleReindexLearningObject : undefined
                                    }
                                    handleViewChunks={isSuperAdmin ? handleViewChunks : undefined}
                                    onEditVisibility={openSingleVisibility}
                                    handleRefetch={
                                        isWebScraperProvider ? handleRefetchPage : undefined
                                    }
                                    handleExclude={
                                        isWebScraperProvider ? setExcludeTarget : undefined
                                    }
                                    isSuperAdmin={!!isSuperAdmin}
                                />
                                <LoaderView show={isLoading} />
                            </div>
                        </>
                    )}
                </div>
                {visibilityDialog && (
                    <VisibilityDialog
                        open={!!visibilityDialog}
                        onClose={() => setVisibilityDialog(null)}
                        single={visibilityDialog.single}
                        count={visibilityDialog.loItemIds.length}
                        initialCitationDisplay={visibilityDialog.citationDisplay}
                        webSite={isWebScraperProvider}
                        onSave={handleSaveVisibility}
                    />
                )}
                <ModalTemplate
                    open={filesUploadOpen}
                    width={658}
                    handleClose={handleFilesUploadClose}
                    ariaLabel="Upload File"
                >
                    <FileUploadView handleClose={handleFilesUploadClose} />
                </ModalTemplate>
                {chunksDialogOpen && chunksDialogLO && user?.currentKnowledgeBaseId && (
                    <RecognitionChunksDialog
                        open={chunksDialogOpen}
                        handleClose={handleChunksDialogClose}
                        learningObject={chunksDialogLO}
                        companyId={user.companyId}
                        knowledgeBaseId={user.currentKnowledgeBaseId}
                    />
                )}
                <ReprocessConfirmationModal
                    open={reprocessTarget !== null}
                    handleClose={() => setReprocessTarget(null)}
                    handleAction={handleReprocessConfirm}
                />
                <ConfirmationPopup
                    open={excludeTarget !== null}
                    type={ConfirmationPopupType.dangerous}
                    icon={TrashIcon}
                    title="Exclude page"
                    description={`${excludeTarget?.name ?? "This page"} is removed from the Assistant and its URL is added to the site's exclusions, so later crawls skip it. Edit the crawl settings to include it again.`}
                    actionName="Exclude"
                    handleAction={handleExcludePageConfirm}
                    handleClose={() => setExcludeTarget(null)}
                />
                <ConfirmationPopup
                    open={deleteLOsConfirmationOpen}
                    type={ConfirmationPopupType.dangerous}
                    icon={TrashIcon}
                    title="Delete"
                    description={`You are trying to delete ${deletableUids.length} learning object(s).
                                This can't be undone!`}
                    actionName="Delete"
                    handleAction={handleDeleteLOsConfirmationAction}
                    handleClose={() => setDeleteLOsConfirmationOpen(false)}
                />
                <ConfirmationPopup
                    open={publishConfirmationOpen}
                    icon={RefreshIcon}
                    title="Publish"
                    subtitle={publishSubtitle}
                    description={publishDescription}
                    actionName="Publish"
                    showCancelButton={false}
                    handleAction={handlePublishConfirmationAction}
                    handleClose={() => setPublishConfirmationOpen(false)}
                />
                <ConfirmationPopup
                    open={unpublishConfirmationOpen}
                    icon={RefreshIcon}
                    title="Unpublish"
                    subtitle={publishSubtitle}
                    description={unpublishDescription}
                    actionName="Unpublish"
                    showCancelButton={false}
                    handleAction={handleUnpublishConfirmationAction}
                    handleClose={() => setUnpublishConfirmationOpen(false)}
                />
            </HeaderLayout>
        </EmptyLeftMenuLayout>
    );
};

export default KnowledgeBaseElements;
